import { z } from 'zod';
import { DEMO_SESSION_DURATION_VALUES, asTimezone } from '@lgc/database/types/domains/club';
import { AUDIENCE_CLASS_VALUES } from '@lgc/database/types/domains/bay';
import { differenceInMinutes, startOfDay, format } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone, toDate } from 'date-fns-tz';

// src/availability/schemas.ts
var TimeSegmentSchema = z.object({
  startMinutes: z.number().int("Start minutes must be an integer").min(0, "Start minutes cannot be negative").max(1439, "Start minutes cannot exceed 1439 (11:59 PM)"),
  endMinutes: z.number().int("End minutes must be an integer").min(0, "End minutes cannot be negative").max(1560, "End minutes cannot exceed 1560 (2:00 AM next day)")
}).refine(
  (data) => data.startMinutes < data.endMinutes,
  { message: "Start time must be before end time" }
).refine(
  // An open window is owned by the day it opens and may spill at most into the
  // next day, so it can never be longer than 24h. endMinutes === startMinutes+1440
  // is a window that closes exactly 24h later (e.g. {0,1440} = the 24/7 day).
  (data) => data.endMinutes <= data.startMinutes + 1440,
  { message: "A window cannot be longer than 24 hours" }
);
var DayScheduleSchema = z.array(TimeSegmentSchema).refine(
  // Segments within a single day must not overlap. Sorted by start, each segment
  // must begin at or after the previous one ends (spillover endMinutes > 1440 is
  // accounted for since the comparison uses raw endMinutes).
  (segments) => {
    const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startMinutes < sorted[i - 1].endMinutes) return false;
    }
    return true;
  },
  { message: "Schedule segments must not overlap" }
);
var WeeklyScheduleSchema = z.object({
  monday: DayScheduleSchema,
  tuesday: DayScheduleSchema,
  wednesday: DayScheduleSchema,
  thursday: DayScheduleSchema,
  friday: DayScheduleSchema,
  saturday: DayScheduleSchema,
  sunday: DayScheduleSchema
});
var DateStringSchema = z.string().regex(
  /^\d{4}-\d{2}-\d{2}$/,
  "Date must be in YYYY-MM-DD format"
);
var ScheduleExceptionKindSchema = z.enum(["hours", "closed"]);
var ScheduleExceptionSchema = z.object({
  date: DateStringSchema,
  kind: ScheduleExceptionKindSchema,
  schedule: DayScheduleSchema,
  reason: z.string().optional(),
  isPublic: z.boolean().optional()
}).refine(
  (data) => data.schedule.length > 0,
  { message: 'An exception must declare at least one segment \u2014 closed all day is kind "closed" with 00:00\u201324:00' }
);
var ScheduleExceptionsSchema = z.record(
  DateStringSchema,
  ScheduleExceptionSchema
);
var MAX_DEMO_ADVANCE_MINUTES = 30 * 24 * 60;
var MAX_LESSON_DURATION_MINUTES = 120;
var DEFAULT_LESSON_DURATION_MINUTES = 60;
var DemoBookingConfigSchema = z.object({
  minAdvanceMinutes: z.number().int("Minimum advance notice must be a whole number of minutes").min(0, "Minimum advance notice cannot be negative").max(MAX_DEMO_ADVANCE_MINUTES, "Minimum advance notice cannot exceed 30 days"),
  // Driven by the values array, not by indexing its slots — adding a third
  // allowed duration widens this schema with the TS type and the settings
  // radio in one edit, instead of silently rejecting the new option.
  sessionDurationMinutes: z.custom(
    (n) => typeof n === "number" && DEMO_SESSION_DURATION_VALUES.includes(n),
    { message: `Session duration must be one of: ${DEMO_SESSION_DURATION_VALUES.join(", ")}` }
  ),
  hours: WeeklyScheduleSchema
});
var UpdateDemoBookingSettingsBodySchema = z.object({
  enabled: z.boolean().optional(),
  config: DemoBookingConfigSchema.optional()
}).refine(
  (body) => body.enabled !== void 0 || body.config !== void 0,
  { message: "At least one field is required: enabled or config" }
);
var AudienceEntrySchema = z.union([
  z.object({ kind: z.enum(AUDIENCE_CLASS_VALUES) }).strict(),
  z.object({ kind: z.literal("plan"), planType: z.literal("membership"), id: z.number().int().positive() }).strict()
]);
var BayAccessSegmentSchema = z.object({
  startMinutes: z.number().int().min(0).max(1439),
  endMinutes: z.number().int().min(0).max(1560),
  audience: z.array(AudienceEntrySchema).min(1, "Choose who may book in this window")
}).strict().refine((s) => s.startMinutes < s.endMinutes, { message: "Start time must be before end time" }).refine((s) => s.endMinutes <= s.startMinutes + 1440, { message: "A window cannot be longer than 24 hours" }).refine(
  (s) => new Set(s.audience.map((a) => a.kind === "plan" ? `plan:${a.id}` : a.kind)).size === s.audience.length,
  { message: "An audience lists each entry once" }
);
var BayAccessDaySchema = z.array(BayAccessSegmentSchema).min(1, "A customized day needs at least one window").refine(
  (segments) => {
    const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startMinutes < sorted[i - 1].endMinutes) return false;
    }
    return true;
  },
  { message: "Windows on one day must not overlap" }
);
var BayAccessScheduleSchema = z.object({
  monday: BayAccessDaySchema.optional(),
  tuesday: BayAccessDaySchema.optional(),
  wednesday: BayAccessDaySchema.optional(),
  thursday: BayAccessDaySchema.optional(),
  friday: BayAccessDaySchema.optional(),
  saturday: BayAccessDaySchema.optional(),
  sunday: BayAccessDaySchema.optional()
}).strict();

// src/availability/core/time.ts
function minutesFromTime(hours, minutes) {
  return hours * 60 + minutes;
}
function timeFromMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
}
function formatTime(totalMinutes, use24Hour = false) {
  const { hours, minutes } = timeFromMinutes(totalMinutes % 1440);
  if (use24Hour) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, "0");
  return `${displayHours}:${displayMinutes} ${period}`;
}
function parseTimeToMinutes(timeString) {
  const cleaned = timeString.trim().toUpperCase();
  const isPM = cleaned.includes("PM");
  const isAM = cleaned.includes("AM");
  const timeOnly = cleaned.replace(/[AP]M/g, "").trim();
  let hours;
  let minutes;
  if (timeOnly.includes(":")) {
    const [h, m] = timeOnly.split(":").map((s) => parseInt(s, 10));
    hours = h;
    minutes = m;
  } else {
    const num = parseInt(timeOnly, 10);
    hours = Math.floor(num / 100);
    minutes = num % 100;
  }
  if (isPM && hours !== 12) {
    hours += 12;
  }
  if (isAM && hours === 12) {
    hours = 0;
  }
  return minutesFromTime(hours, minutes);
}

// src/datetime/branded.ts
function asUtcDate(d) {
  return d;
}
function asZonedDate(d) {
  return d;
}
function asClubMinutes(n) {
  return n;
}

// src/availability/core/segments.ts
function isTimeInSegment(timeMinutes, segment) {
  return timeMinutes >= segment.startMinutes && timeMinutes < segment.endMinutes;
}
function isRangeInSegment(startMinutes, durationMinutes, segment) {
  const endMinutes = startMinutes + durationMinutes;
  return startMinutes >= segment.startMinutes && endMinutes <= segment.endMinutes;
}
function segmentsOverlap(a, b) {
  return a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;
}
function isClosedAllDay(segments) {
  return segments.length === 0;
}
function mergeSegments(segments) {
  if (segments.length === 0) return [];
  const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const lastMerged = merged[merged.length - 1];
    if (current.startMinutes <= lastMerged.endMinutes) {
      lastMerged.endMinutes = Math.max(lastMerged.endMinutes, current.endMinutes);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}
function intersectSegments(a, b) {
  if (a.length === 0 || b.length === 0) return [];
  const overlaps = [];
  for (const left of a) {
    for (const right of b) {
      const hit = getSegmentIntersection(left, right);
      if (hit) overlaps.push(hit);
    }
  }
  return mergeSegments(overlaps);
}
function normalizeSegments(segments) {
  const valid = segments.filter((s) => s.startMinutes < s.endMinutes);
  return mergeSegments(valid);
}
function getSegmentIntersection(a, b) {
  if (!segmentsOverlap(a, b)) return null;
  return {
    startMinutes: Math.max(a.startMinutes, b.startMinutes),
    endMinutes: Math.min(a.endMinutes, b.endMinutes)
  };
}
function getSegmentDuration(segment) {
  return segment.endMinutes - segment.startMinutes;
}
function createFullDaySegment() {
  return { startMinutes: 0, endMinutes: 1440 };
}
function getTotalSegmentMinutes(segments) {
  return segments.reduce((total, segment) => total + getSegmentDuration(segment), 0);
}
function toClubMinutes(utcDate, timezone) {
  const clubTime = toZonedTime(utcDate, timezone);
  return asClubMinutes(differenceInMinutes(clubTime, startOfDay(clubTime)));
}
var _DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];
function getDayOfWeekTz(date, timezone) {
  const zonedDate = toZonedTime(date, timezone);
  return _DAY_NAMES[zonedDate.getDay()];
}
function formatDateKeyTz(date, timezone) {
  const zonedDate = toZonedTime(date, timezone);
  return format(zonedDate, "yyyy-MM-dd");
}
function isWeekendTz(date, timezone) {
  const day = toZonedTime(date, timezone).getDay();
  return day === 0 || day === 6;
}
function isWeekdayTz(date, timezone) {
  return !isWeekendTz(date, timezone);
}

// src/availability/schedule/dayOfWeek.ts
var DAYS_OF_WEEK = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];

// src/availability/schedule/subtract.ts
var END_OF_DAY = 1440;
var MAX_SPILLOVER = 1560;
function subtractDaySchedules(open, closed) {
  if (closed.length === 0) return mergeSegments(open);
  if (open.length === 0) return [];
  const extended = closed.map((segment) => ({
    startMinutes: segment.startMinutes,
    endMinutes: segment.endMinutes === END_OF_DAY ? MAX_SPILLOVER : segment.endMinutes
  }));
  let remaining = open.map((segment) => ({ ...segment }));
  for (const closure of extended) {
    remaining = remaining.flatMap((segment) => subtractOne(segment, closure));
  }
  return mergeSegments(remaining);
}
function subtractOne(segment, closure) {
  if (closure.endMinutes <= segment.startMinutes || closure.startMinutes >= segment.endMinutes) {
    return [segment];
  }
  const pieces = [];
  if (closure.startMinutes > segment.startMinutes) {
    pieces.push({ startMinutes: segment.startMinutes, endMinutes: closure.startMinutes });
  }
  if (closure.endMinutes < segment.endMinutes) {
    pieces.push({ startMinutes: closure.endMinutes, endMinutes: segment.endMinutes });
  }
  return pieces;
}

// src/availability/schedule/effective.ts
function getScheduleForDate(date, baseSchedule, exceptions, timezone) {
  if (exceptions) {
    const dateKey = formatDateKeyTz(date, timezone);
    const exception = exceptions[dateKey];
    if (exception) {
      const weekly = baseSchedule[getDayOfWeekTz(date, timezone)] || [];
      return exception.kind === "closed" ? subtractDaySchedules(weekly, exception.schedule) : exception.schedule;
    }
  }
  const dayOfWeek = getDayOfWeekTz(date, timezone);
  return baseSchedule[dayOfWeek] || [];
}
function isFullDayClosure(exception) {
  return exception.kind === "closed" && exception.schedule.some((s) => s.startMinutes === 0 && s.endMinutes >= 1440);
}
function isPublicScheduleException(exception) {
  return exception.isPublic !== false;
}
function toPublicScheduleExceptions(exceptions) {
  if (!exceptions) return null;
  return Object.fromEntries(
    Object.entries(exceptions).map(([date, exception]) => {
      if (isPublicScheduleException(exception)) return [date, exception];
      const { reason: _reason, ...rest } = exception;
      return [date, rest];
    })
  );
}
function hasScheduleExceptionTz(date, timezone, exceptions) {
  if (!exceptions) return false;
  const dateKey = formatDateKeyTz(date, timezone);
  return dateKey in exceptions;
}
function getScheduleExceptionTz(date, timezone, exceptions) {
  if (!exceptions) return void 0;
  const dateKey = formatDateKeyTz(date, timezone);
  return exceptions[dateKey];
}
function isSameDayInTimezone(date1, date2, timezone) {
  return formatDateKeyTz(date1, timezone) === formatDateKeyTz(date2, timezone);
}
function getDayScheduleForDateTz(date, timezone, weeklySchedule, exceptions) {
  return getScheduleForDate(date, weeklySchedule, exceptions, timezone);
}

// src/datetime/errors.ts
var DateTimeLibraryError = class extends Error {
  constructor(type, message, details) {
    super(message);
    this.name = "DateTimeLibraryError";
    this.type = type;
    this.details = details;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
};
var InvalidTimezoneError = class extends DateTimeLibraryError {
  constructor(message) {
    super("INVALID_TIMEZONE", message);
    this.name = "InvalidTimezoneError";
  }
};
var InvalidDateError = class extends DateTimeLibraryError {
  constructor(message) {
    super("INVALID_DATE", message);
    this.name = "InvalidDateError";
  }
};
function asDateString(s) {
  return s;
}

// src/datetime/constants.ts
var SUPPORTED_TIMEZONES = [
  // Fallback (not a picker option — see note above)
  "UTC",
  // US
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Phoenix",
  // Canada
  "America/Toronto",
  "America/Vancouver",
  "America/Halifax",
  "America/Winnipeg",
  "America/Edmonton",
  // Mexico
  "America/Mexico_City",
  "America/Cancun",
  "America/Tijuana"
];
asTimezone("America/New_York");
var DATE_FORMATS = {
  // "Oct 15, 2025 2:30 PM"
  ISO_DATE: "yyyy-MM-dd",
  // Alias for SHORT_DATE_TIME
  TIME_12H: "h:mm a",
  // Alias for TIME_ONLY
  TIME_24H: "HH:mm"
  // Alias for ISO_TIME
};
var WallClockDate = class extends Date {
  getFullYear() {
    return this.getUTCFullYear();
  }
  getMonth() {
    return this.getUTCMonth();
  }
  getDate() {
    return this.getUTCDate();
  }
  getDay() {
    return this.getUTCDay();
  }
  getHours() {
    return this.getUTCHours();
  }
  getMinutes() {
    return this.getUTCMinutes();
  }
  getSeconds() {
    return this.getUTCSeconds();
  }
  getMilliseconds() {
    return this.getUTCMilliseconds();
  }
  getTimezoneOffset() {
    return 0;
  }
  setFullYear(...args) {
    return this.setUTCFullYear(...args);
  }
  setMonth(...args) {
    return this.setUTCMonth(...args);
  }
  setDate(...args) {
    return this.setUTCDate(...args);
  }
  setHours(...args) {
    return this.setUTCHours(...args);
  }
  setMinutes(...args) {
    return this.setUTCMinutes(...args);
  }
  setSeconds(...args) {
    return this.setUTCSeconds(...args);
  }
  setMilliseconds(...args) {
    return this.setUTCMilliseconds(...args);
  }
};
var clockReaders = /* @__PURE__ */ new Map();
function clockReaderFor(timezone) {
  let reader = clockReaders.get(timezone);
  if (!reader) {
    reader = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    clockReaders.set(timezone, reader);
  }
  return reader;
}
function toWallClock(instant, timezone) {
  const parts = {};
  for (const part of clockReaderFor(timezone).formatToParts(instant)) {
    parts[part.type] = Number(part.value);
  }
  const ms = instant.getTime();
  return new WallClockDate(
    Date.UTC(
      parts.year ?? 0,
      (parts.month ?? 1) - 1,
      parts.day ?? 1,
      // Engines that ignore `hourCycle` write midnight as 24.
      (parts.hour ?? 0) % 24,
      parts.minute ?? 0,
      parts.second ?? 0,
      (ms % 1e3 + 1e3) % 1e3
    )
  );
}
function namesTheZoneOrInstant(formatString) {
  const tokens = formatString.replace(/''/g, "").replace(/'[^']*'/g, "");
  return /[zOxXtT]/.test(tokens);
}
function formatWallClock(instant, timezone, formatString) {
  if (namesTheZoneOrInstant(formatString)) {
    return formatInTimeZone(instant, timezone, formatString);
  }
  return format(toWallClock(toDate(instant), timezone), formatString);
}

// src/datetime/timeString.ts
var END_OF_DAY2 = "24:00";
function isEndOfDaySentinel(timeString) {
  return timeString === END_OF_DAY2 || timeString === "24:00:00";
}
var MAX_OVERFLOW_HOUR = 47;
var TIME_PATTERN = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/;
function parseTimeOfDay(timeString, options = {}) {
  if (!timeString || typeof timeString !== "string") {
    throw new InvalidDateError("Time string must be a non-empty string");
  }
  const match = TIME_PATTERN.exec(timeString);
  if (!match) {
    throw new InvalidDateError(
      `Invalid time format: ${timeString}. Expected HH:mm or HH:mm:ss`
    );
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3] === void 0 ? 0 : Number(match[3]);
  if (minutes > 59) {
    throw new InvalidDateError(`Invalid minute: ${minutes}. Expected 0-59`);
  }
  if (seconds > 59) {
    throw new InvalidDateError(`Invalid second: ${seconds}. Expected 0-59`);
  }
  if (hours > 23) {
    if (options.allowDayOverflow) {
      if (hours > MAX_OVERFLOW_HOUR) {
        throw new InvalidDateError(
          `Invalid hour: ${hours}. Expected 0-${MAX_OVERFLOW_HOUR} when day overflow is allowed`
        );
      }
      return { hours, minutes, seconds };
    }
    if (options.allowEndOfDay && hours === 24 && minutes === 0 && seconds === 0) {
      return { hours, minutes, seconds };
    }
    if (hours === 24 && minutes === 0 && seconds === 0) {
      throw new InvalidDateError(
        `Hour 24 is the end-of-day sentinel, not a wall-clock time. Use createClubDateTimeFromSlot() for slot times that may be "${END_OF_DAY2}".`
      );
    }
    throw new InvalidDateError(
      `Invalid hour: ${hours}. Expected 0-23${options.allowEndOfDay ? ` (or ${END_OF_DAY2} for end-of-day)` : ""}`
    );
  }
  return { hours, minutes, seconds };
}
function pad2(n) {
  return String(n).padStart(2, "0");
}

// src/datetime/converters.ts
function validateDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new InvalidDateError("Invalid date provided");
  }
}
function validateTimezone(timezone) {
  if (!timezone || typeof timezone !== "string") {
    throw new InvalidTimezoneError("Timezone must be a non-empty string");
  }
  if (!SUPPORTED_TIMEZONES.includes(timezone)) {
    throw new InvalidTimezoneError(
      `Unsupported timezone: ${timezone}. Must be one of: ${SUPPORTED_TIMEZONES.join(", ")}`
    );
  }
}
function utcToClubTime(utcDate, timezone) {
  validateDate(utcDate);
  validateTimezone(timezone);
  return asZonedDate(toZonedTime(utcDate, timezone));
}
function clubTimeToUtc(clubDate, timezone) {
  validateDate(clubDate);
  validateTimezone(timezone);
  return asUtcDate(fromZonedTime(clubDate, timezone));
}
function minutesToTimeString(minutes) {
  if (minutes < 0 || minutes > 1440) {
    throw new InvalidDateError(`Minutes must be between 0 and 1440. Received: ${minutes}`);
  }
  if (minutes === 1440) {
    return END_OF_DAY2;
  }
  return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
}
function formatClubTime(utcDate, timezone, use24Hour = false) {
  const formatString = use24Hour ? DATE_FORMATS.TIME_24H : DATE_FORMATS.TIME_12H;
  return formatWallClock(utcDate, timezone, formatString);
}
function formatClubDateISO(utcDate, timezone) {
  return asDateString(formatWallClock(utcDate, timezone, DATE_FORMATS.ISO_DATE));
}
function formatDuration(minutes) {
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `${hours} hour${hours === 1 ? "" : "s"} ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}`;
}
function formatTimeComponents(hour, minute, use24Hour) {
  if (use24Hour) {
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = String(minute).padStart(2, "0");
  return `${displayHour}:${displayMinute} ${period}`;
}
function formatTime2(time, use24Hour = false) {
  if (isEndOfDaySentinel(time)) {
    return formatTimeComponents(0, 0, use24Hour);
  }
  const { hours, minutes } = parseTimeOfDay(time, { allowDayOverflow: true });
  return formatTimeComponents(hours % 24, minutes, use24Hour);
}

// src/availability/schedule/exceptionNotice.ts
var formatMinutes = (minutes) => formatTime2(minutesToTimeString(minutes), false);
function formatRanges(segments) {
  return segments.map((segment) => `${formatMinutes(segment.startMinutes)} \u2013 ${formatMinutes(segment.endMinutes % 1440)}`).join(", ");
}
function describeScheduleException({
  kind,
  reason,
  isClosedAllDay: isClosedAllDay2,
  segments
}) {
  const trimmedReason = reason?.trim();
  const title = trimmedReason ? trimmedReason : kind === "hours" ? "Special hours" : null;
  if (kind === "closed") {
    return {
      title,
      detail: isClosedAllDay2 ? "Closed all day" : `Closed ${formatRanges(segments)}`
    };
  }
  return { title, detail: `Open ${formatRanges(segments)}` };
}
function exceptionNoticeFor(day) {
  if (!day?.isException || !day.kind || day.isPublic === false) return null;
  return describeScheduleException({
    kind: day.kind,
    reason: day.reason,
    isClosedAllDay: day.isClosedAllDay,
    segments: day.exceptionSegments
  });
}
function scheduleExceptionNotice(exception) {
  if (!exception || !isPublicScheduleException(exception)) return null;
  return describeScheduleException({
    kind: exception.kind,
    reason: exception.reason,
    isClosedAllDay: isFullDayClosure(exception),
    segments: [...exception.schedule].sort((a, b) => a.startMinutes - b.startMinutes)
  });
}

// src/availability/schedule/merge.ts
function mergeDaySchedules(schedule1, schedule2) {
  if (!schedule2 || schedule2.length === 0) return schedule1;
  if (!schedule1 || schedule1.length === 0) return schedule2;
  return mergeSegments([...schedule1, ...schedule2]);
}
function mergeWeeklySchedules(schedule1, schedule2) {
  if (!schedule2) return schedule1;
  return {
    monday: mergeDaySchedules(schedule1.monday, schedule2.monday),
    tuesday: mergeDaySchedules(schedule1.tuesday, schedule2.tuesday),
    wednesday: mergeDaySchedules(schedule1.wednesday, schedule2.wednesday),
    thursday: mergeDaySchedules(schedule1.thursday, schedule2.thursday),
    friday: mergeDaySchedules(schedule1.friday, schedule2.friday),
    saturday: mergeDaySchedules(schedule1.saturday, schedule2.saturday),
    sunday: mergeDaySchedules(schedule1.sunday, schedule2.sunday)
  };
}
function mergePublicAndMembershipHours(publicHours, membershipHours, membershipEnabled = false) {
  if (!membershipEnabled || !membershipHours) return publicHours;
  return mergeWeeklySchedules(publicHours, membershipHours);
}

// src/availability/schedule/intersect.ts
function intersectDaySchedules(a, b) {
  return intersectSegments(a, b);
}
function intersectWeeklySchedules(schedule1, schedule2) {
  if (!schedule2) return schedule1;
  return {
    monday: intersectDaySchedules(schedule1.monday, schedule2.monday),
    tuesday: intersectDaySchedules(schedule1.tuesday, schedule2.tuesday),
    wednesday: intersectDaySchedules(schedule1.wednesday, schedule2.wednesday),
    thursday: intersectDaySchedules(schedule1.thursday, schedule2.thursday),
    friday: intersectDaySchedules(schedule1.friday, schedule2.friday),
    saturday: intersectDaySchedules(schedule1.saturday, schedule2.saturday),
    sunday: intersectDaySchedules(schedule1.sunday, schedule2.sunday)
  };
}
function minutesToHHMM(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}
function shiftDateISO(dateISO, offset) {
  const [y, m, d] = dateISO.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + offset);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
function clubWallToUtc(dayISO, hhmm, timezone) {
  return asUtcDate(fromZonedTime(`${dayISO}T${hhmm}:00`, timezone));
}
function slotInstant(dayISO, minutes, timezone) {
  const dayOffset = Math.floor(minutes / 1440);
  const withinDay = minutes % 1440;
  return clubWallToUtc(shiftDateISO(dayISO, dayOffset), minutesToHHMM(withinDay), timezone);
}
function materializeSegment(dayISO, segment, timezone) {
  return {
    start: slotInstant(dayISO, segment.startMinutes, timezone),
    end: slotInstant(dayISO, segment.endMinutes, timezone)
  };
}
function mergeWindows(windows) {
  if (windows.length === 0) return [];
  const sorted = [...windows].sort((a, b) => a.start.getTime() - b.start.getTime());
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const last = merged[merged.length - 1];
    if (cur.start.getTime() <= last.end.getTime()) {
      if (cur.end.getTime() > last.end.getTime()) last.end = cur.end;
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}
function resolveOpenWindowsFrom(instant, timezone, provider) {
  const dayISO = formatClubDateISO(instant, timezone);
  const windows = [];
  for (const offset of [-1, 0, 1]) {
    const targetISO = shiftDateISO(dayISO, offset);
    const anchor = clubWallToUtc(targetISO, "12:00", timezone);
    for (const segment of provider(anchor)) {
      windows.push(materializeSegment(targetISO, segment, timezone));
    }
  }
  return mergeWindows(windows);
}
function resolveOpenWindows(instant, schedule, timezone, exceptions) {
  return resolveOpenWindowsFrom(
    instant,
    timezone,
    (anchor) => getScheduleForDate(anchor, schedule, exceptions, timezone)
  );
}
function isIntervalWithinWindows(windows, startTime, endTime) {
  return windows.some(
    (w) => w.start.getTime() <= startTime.getTime() && endTime.getTime() <= w.end.getTime()
  );
}
function isIntervalWithinSchedule(startTime, endTime, schedule, timezone, exceptions) {
  return isIntervalWithinWindows(
    resolveOpenWindows(startTime, schedule, timezone, exceptions),
    startTime,
    endTime
  );
}

// src/availability/schedule/validation.ts
function isSlotInWeeklySchedule(time, schedule, timezone) {
  const dayOfWeek = getDayOfWeekTz(time, timezone);
  const daySchedule = schedule[dayOfWeek];
  if (!daySchedule || daySchedule.length === 0) {
    return false;
  }
  const timeInClubTz = toZonedTime(time, timezone);
  const timeMinutes = timeInClubTz.getHours() * 60 + timeInClubTz.getMinutes();
  return daySchedule.some(
    (segment) => timeMinutes >= segment.startMinutes && timeMinutes < segment.endMinutes
  );
}
function isBookingWithinSchedule(startTime, endTime, weeklySchedule, timezone, exceptions) {
  if (isIntervalWithinSchedule(startTime, endTime, weeklySchedule, timezone, exceptions)) {
    return { isWithinHours: true };
  }
  const startDaySchedule = getScheduleForDate(startTime, weeklySchedule, exceptions, timezone);
  if (isClosedAllDay(startDaySchedule)) {
    return { isWithinHours: false, reason: "Facility is closed on this day" };
  }
  return { isWithinHours: false, reason: "Booking time is outside operating hours" };
}

// src/availability/schedule/helpers.ts
var FULL_DAY_SEGMENT = { startMinutes: 0, endMinutes: 1440 };
var ALL_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];
function isFullDaySchedule(daySchedule) {
  return daySchedule.length === 1 && daySchedule[0].startMinutes === FULL_DAY_SEGMENT.startMinutes && daySchedule[0].endMinutes === FULL_DAY_SEGMENT.endMinutes;
}
function is24_7Schedule(schedule) {
  if (!schedule) return false;
  return ALL_DAYS.every((day) => {
    const daySchedule = schedule[day];
    return daySchedule != null && isFullDaySchedule(daySchedule);
  });
}
function create24_7Schedule() {
  return {
    monday: [{ ...FULL_DAY_SEGMENT }],
    tuesday: [{ ...FULL_DAY_SEGMENT }],
    wednesday: [{ ...FULL_DAY_SEGMENT }],
    thursday: [{ ...FULL_DAY_SEGMENT }],
    friday: [{ ...FULL_DAY_SEGMENT }],
    saturday: [{ ...FULL_DAY_SEGMENT }],
    sunday: [{ ...FULL_DAY_SEGMENT }]
  };
}

// src/availability/slots/checking.ts
function isClubOpen(timeMinutes, schedule) {
  if (isClosedAllDay(schedule)) return false;
  return schedule.some((segment) => isTimeInSegment(timeMinutes, segment));
}
function checkAvailability(startMinutes, durationMinutes, schedule) {
  if (isClosedAllDay(schedule)) {
    return {
      isAvailable: false,
      reason: "Facility is closed on this day"
    };
  }
  const fitsInSegment = schedule.some(
    (segment) => isRangeInSegment(startMinutes, durationMinutes, segment)
  );
  if (fitsInSegment) {
    return { isAvailable: true };
  }
  const nextSegment = schedule.find(
    (segment) => segment.startMinutes >= startMinutes && // Measure from the segment's own start, not the requested startMinutes.
    // Using startMinutes here would pass a segment that is too short to hold
    // the duration (e.g. start=0, duration=600, segment 480–720: 720>=600 ✓
    // but 480+600=1080 > 720 — the slot doesn't actually fit).
    segment.startMinutes + durationMinutes <= segment.endMinutes
  );
  return {
    isAvailable: false,
    reason: "Requested time slot is outside operating hours",
    nextAvailableTime: nextSegment?.startMinutes
  };
}
function getAvailableSegments(schedule) {
  return normalizeSegments(schedule);
}
function getTotalOpenMinutes(schedule) {
  return getAvailableSegments(schedule).reduce(
    (total, segment) => total + (segment.endMinutes - segment.startMinutes),
    0
  );
}
function hasOpenHours(schedule) {
  return !isClosedAllDay(schedule);
}

// src/availability/slots/startGrid.ts
function minuteOfHour(startMinutes) {
  return (startMinutes % 60 + 60) % 60;
}
function startMarksRemainder(marks, intervalMinutes) {
  return marks[0] % intervalMinutes;
}
function firstOnGrid(fromMinutes, remainder, intervalMinutes) {
  const shift = ((remainder - fromMinutes) % intervalMinutes + intervalMinutes) % intervalMinutes;
  return fromMinutes + shift;
}
function isValidStartMarks(marks, intervalMinutes) {
  if (marks.length === 0) return false;
  if (!marks.every((m) => Number.isInteger(m) && m >= 0 && m < 60 && m % 5 === 0)) return false;
  if (!marks.every((m, i) => i === 0 || m > marks[i - 1])) return false;
  const remainder = startMarksRemainder(marks, intervalMinutes);
  return marks.every((m) => m % intervalMinutes === remainder);
}
function startGridOptions(intervalMinutes) {
  const options = [];
  for (let remainder = 0; remainder < Math.min(intervalMinutes, 60); remainder += 5) {
    const marks = [];
    for (let m = remainder; m < 60; m += intervalMinutes) marks.push(m);
    options.push({ remainder, marks });
  }
  return options;
}
function startMarksCoverage(marks, intervalMinutes) {
  const remainder = startMarksRemainder(marks, intervalMinutes);
  const grid = [];
  for (let m = remainder; m < 60; m += intervalMinutes) grid.push(m);
  return { perHour: marks.length, gridPerHour: grid.length, missing: grid.filter((m) => !marks.includes(m)) };
}
function canStartAt(startMinutes, marks) {
  return !marks || marks.includes(minuteOfHour(startMinutes));
}
function isStartableRow(row) {
  return row.canStart !== false && (row.startBayIds === void 0 || row.startBayIds.length > 0);
}
function isOnStartGrid(schedule, startMinutes, intervalMinutes, marks = null) {
  const segment = segmentAt(schedule, startMinutes);
  if (!segment) return null;
  if (!marks) {
    const fromAnchor = startMinutes - segment.startMinutes;
    return fromAnchor % intervalMinutes === 0;
  }
  return canStartAt(startMinutes, marks);
}
function startMarksLabel(marks) {
  const labels = marks.map((m) => `:${String(m).padStart(2, "0")}`);
  return labels.length <= 1 ? labels.join("") : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}
function startMarksAt(schedule, startMinutes, intervalMinutes, marks = null) {
  if (marks) return marks;
  const segment = segmentAt(schedule, startMinutes);
  if (!segment) return null;
  const remainder = minuteOfHour(segment.startMinutes) % intervalMinutes;
  return startGridOptions(intervalMinutes).find((g) => g.remainder === remainder)?.marks ?? null;
}
function plainGridRemainderAt(schedule, minutes, intervalMinutes) {
  const segment = segmentAt(schedule, minutes);
  return segment ? segment.startMinutes % intervalMinutes : null;
}
function segmentAt(schedule, startMinutes) {
  return getAvailableSegments(schedule).find(
    (s) => startMinutes >= s.startMinutes && startMinutes < s.endMinutes
  );
}

// src/availability/slots/generation.ts
function generateBookingSlots(schedule, durationMinutes, intervalMinutes = durationMinutes, options = {}) {
  const {
    use24Hour = false,
    includeUnavailable = false,
    minTime = 0,
    maxTime = 1440,
    startMarks = null
  } = options;
  const slots = [];
  const remainder = startMarks ? startMarksRemainder(startMarks, intervalMinutes) : 0;
  const firstRow = (anchor) => startMarks ? firstOnGrid(anchor, remainder, intervalMinutes) : anchor;
  const marked = (time) => startMarks ? { canStart: canStartAt(time, startMarks) } : {};
  if (isClosedAllDay(schedule)) {
    if (includeUnavailable) {
      for (let time = firstRow(minTime); time < maxTime; time += intervalMinutes) {
        slots.push({
          startMinutes: time,
          durationMinutes,
          displayTime: formatTime(time, use24Hour),
          ...marked(time),
          isAvailable: false,
          unavailableReason: "Facility is closed on this day"
        });
      }
    }
    return slots;
  }
  const availableSegments = getAvailableSegments(schedule);
  for (const segment of availableSegments) {
    const segmentStart = Math.max(segment.startMinutes, minTime);
    const segmentEnd = Math.min(segment.endMinutes, maxTime);
    for (let time = firstRow(segmentStart); time < segmentEnd; time += intervalMinutes) {
      const endTime = time + durationMinutes;
      const fitsInSegment = endTime <= segmentEnd;
      if (fitsInSegment) {
        slots.push({
          startMinutes: time,
          durationMinutes,
          displayTime: formatTime(time, use24Hour),
          ...marked(time),
          isAvailable: true
        });
      } else if (includeUnavailable) {
        const unavailableReason = endTime > segment.endMinutes ? "Slot extends beyond operating hours" : "Slot extends beyond requested time window";
        slots.push({
          startMinutes: time,
          durationMinutes,
          displayTime: formatTime(time, use24Hour),
          ...marked(time),
          isAvailable: false,
          unavailableReason
        });
      }
    }
  }
  if (includeUnavailable && availableSegments.length > 0) {
    const firstSegment = availableSegments[0];
    if (firstSegment.startMinutes > minTime) {
      for (let time = firstRow(minTime); time < firstSegment.startMinutes; time += intervalMinutes) {
        slots.push({
          startMinutes: time,
          durationMinutes,
          displayTime: formatTime(time, use24Hour),
          ...marked(time),
          isAvailable: false,
          unavailableReason: "Outside operating hours"
        });
      }
    }
    for (let i = 0; i < availableSegments.length - 1; i++) {
      const currentSegment = availableSegments[i];
      const nextSegment = availableSegments[i + 1];
      for (let time = firstRow(currentSegment.endMinutes); time < nextSegment.startMinutes; time += intervalMinutes) {
        slots.push({
          startMinutes: time,
          durationMinutes,
          displayTime: formatTime(time, use24Hour),
          ...marked(time),
          isAvailable: false,
          unavailableReason: "Facility closed during this time"
        });
      }
    }
    const lastSegment = availableSegments[availableSegments.length - 1];
    if (lastSegment.endMinutes < maxTime) {
      for (let time = firstRow(lastSegment.endMinutes); time < maxTime; time += intervalMinutes) {
        slots.push({
          startMinutes: time,
          durationMinutes,
          displayTime: formatTime(time, use24Hour),
          ...marked(time),
          isAvailable: false,
          unavailableReason: "Outside operating hours"
        });
      }
    }
    slots.sort((a, b) => a.startMinutes - b.startMinutes);
  }
  return slots;
}
function filterAvailableSlots(slots) {
  return slots.filter((slot) => slot.isAvailable);
}
function findNextAvailableSlot(slots, fromMinutes) {
  return slots.find((slot) => slot.isAvailable && slot.startMinutes >= fromMinutes) ?? null;
}

// src/availability/primeTime/detection.ts
function isPrimeTime(timeMinutes, primeTimeSchedule) {
  if (!primeTimeSchedule || primeTimeSchedule.length === 0) {
    return false;
  }
  return primeTimeSchedule.some((segment) => isTimeInSegment(timeMinutes, segment));
}
function isPrimeTimeSlot(startMinutes, durationMinutes, primeTimeSchedule) {
  if (!primeTimeSchedule || primeTimeSchedule.length === 0) {
    return false;
  }
  return primeTimeSchedule.some(
    (segment) => isRangeInSegment(startMinutes, durationMinutes, segment)
  );
}
function overlapsPrimeTime(startMinutes, durationMinutes, primeTimeSchedule) {
  if (!primeTimeSchedule || primeTimeSchedule.length === 0) {
    return false;
  }
  const endMinutes = startMinutes + durationMinutes;
  return primeTimeSchedule.some(
    (segment) => startMinutes < segment.endMinutes && endMinutes > segment.startMinutes
  );
}
function getPrimeTimeScheduleForDate(date, timezone, primeTimeConfig) {
  if (!primeTimeConfig || !primeTimeConfig.hours) {
    return [];
  }
  const dayOfWeek = getDayOfWeekTz(date, timezone);
  return primeTimeConfig.hours[dayOfWeek] || [];
}
function getPrimeTimePercentage(startMinutes, durationMinutes, primeTimeSchedule) {
  if (!primeTimeSchedule || primeTimeSchedule.length === 0) {
    return 0;
  }
  const slotEnd = startMinutes + durationMinutes;
  let primeTimeMinutes = 0;
  for (const segment of primeTimeSchedule) {
    const overlapStart = Math.max(startMinutes, segment.startMinutes);
    const overlapEnd = Math.min(slotEnd, segment.endMinutes);
    if (overlapStart < overlapEnd) {
      primeTimeMinutes += overlapEnd - overlapStart;
    }
  }
  return Math.round(primeTimeMinutes / durationMinutes * 100);
}
function timeRangesOverlap(start1, end1, start2, end2) {
  return start1 < end2 && start2 < end1;
}
function hasBookingConflict(proposedStart, proposedEnd, existingBookings) {
  if (!existingBookings || existingBookings.length === 0) {
    return false;
  }
  return existingBookings.some((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    return timeRangesOverlap(proposedStart, proposedEnd, bookingStart, bookingEnd);
  });
}
function findConflictingBookings(proposedStart, proposedEnd, existingBookings) {
  if (!existingBookings || existingBookings.length === 0) {
    return [];
  }
  return existingBookings.filter((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    return timeRangesOverlap(proposedStart, proposedEnd, bookingStart, bookingEnd);
  });
}
function getBookingsForDate(date, allBookings, timezone) {
  const startOfDay2 = createDateFromMinutes(date, 0, timezone);
  const endOfDay = createDateFromMinutes(date, 1439, timezone);
  return allBookings.filter((booking) => {
    const bookingStart = new Date(booking.startTime);
    return bookingStart >= startOfDay2 && bookingStart <= endOfDay;
  });
}
function isSlotConflicted(date, startMinutes, durationMinutes, existingBookings, timezone) {
  const slotStart = createDateFromMinutes(date, startMinutes, timezone);
  const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60 * 1e3);
  return hasBookingConflict(slotStart, slotEnd, existingBookings);
}
function createDateFromMinutes(date, minutes, timezone) {
  if (timezone) {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
    const mm = String(minutes % 60).padStart(2, "0");
    return fromZonedTime(`${y}-${m}-${d}T${hh}:${mm}:00`, timezone);
  }
  const result = new Date(date);
  result.setUTCHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return result;
}
function hasBookingConflictForResource(resourceId, proposedStart, proposedEnd, existingBookings, excludeBookingId) {
  const resourceBookings = existingBookings.filter((booking) => {
    const matchesResource = booking.bayId !== void 0 && booking.bayId === resourceId || booking.resourceId !== void 0 && booking.resourceId === resourceId;
    const shouldExclude = excludeBookingId !== void 0 && booking.id !== void 0 && booking.id === excludeBookingId;
    return matchesResource && !shouldExclude;
  });
  return hasBookingConflict(proposedStart, proposedEnd, resourceBookings);
}

// src/availability/constraints/durations.ts
function getValidDurations(maxSessionMinutes, slotIntervalMinutes, minSessionMinutes = null) {
  if (slotIntervalMinutes <= 0 || maxSessionMinutes <= 0) return [];
  const floor = minSessionMinutes ?? 0;
  const durations = [];
  for (let d = slotIntervalMinutes; d <= maxSessionMinutes; d += slotIntervalMinutes) {
    if (d >= floor) durations.push(d);
  }
  return durations;
}

// src/availability/constraints/validation.ts
function validateDurationsFromConstraints(requestedDurations, maxSessionMinutes, slotIntervalMinutes) {
  const validDurations = getValidDurations(maxSessionMinutes, slotIntervalMinutes);
  if (validDurations.length === 0) {
    return {
      valid: false,
      validDurations: [],
      invalidDurations: requestedDurations,
      message: "No valid durations configured for this club"
    };
  }
  const invalidDurations = requestedDurations.filter(
    (d) => !validDurations.includes(d)
  );
  if (invalidDurations.length > 0) {
    return {
      valid: false,
      validDurations,
      invalidDurations,
      message: `Invalid durations: ${invalidDurations.join(", ")}. Valid options: ${validDurations.join(", ")}`
    };
  }
  return { valid: true, validDurations, invalidDurations: [] };
}

// src/availability/constraints/advanceBooking.ts
function resolveAdvanceBookingDays(clubDefault, templateOverride, instanceOverride) {
  return instanceOverride ?? templateOverride ?? clubDefault;
}

// src/availability/constraints/bookingStartWindow.ts
var MINUTE_MS = 6e4;
var DAY_MINUTES = 24 * 60;
function earliestBookableStart(now, { noticeMinutes = 0, graceMinutes = 0 } = {}) {
  const nowMs = typeof now === "number" ? now : now.getTime();
  return asUtcDate(new Date(nowMs + (noticeMinutes - graceMinutes) * MINUTE_MS));
}
function isStartTooSoon(start, window = {}, now = Date.now()) {
  return start.getTime() < earliestBookableStart(now, window).getTime();
}
function advanceWindowClearsNotice(days, noticeMinutes) {
  return days * DAY_MINUTES > noticeMinutes;
}
var PAST_START_API_REFUSAL = "You cannot book a time in the past. Please choose a future time.";
var START_PASSED_REFUSAL = "Selected time has already passed. Please choose a future time slot.";
function minNoticeRefusal(noticeMinutes) {
  if (!noticeMinutes || noticeMinutes <= 0) return "That time is too soon. Please choose a later start.";
  return `Bookings here need at least ${formatDuration(noticeMinutes)} of notice. Please choose a later start.`;
}

// src/availability/constraints/sessionLength.ts
function checkSessionLength(durationMinutes, bounds) {
  const min = bounds.minSessionMinutes ?? null;
  if (min !== null && durationMinutes < min) return { reason: "too_short", limitMinutes: min };
  if (durationMinutes > bounds.maxSessionMinutes) return { reason: "too_long", limitMinutes: bounds.maxSessionMinutes };
  const step = bounds.intervalMinutes;
  if (step && step > 0 && durationMinutes % step !== 0) return { reason: "off_step", limitMinutes: step };
  return null;
}
function sessionLengthMessage(violation) {
  switch (violation.reason) {
    case "too_short":
      return `Bookings here must be at least ${formatMinutes2(violation.limitMinutes)}`;
    case "too_long":
      return `Bookings here can be at most ${formatMinutes2(violation.limitMinutes)}`;
    case "off_step":
      return violation.limitMinutes === 60 ? "Bookings here run in whole hours" : `Bookings here run in ${violation.limitMinutes}-minute steps`;
  }
}
function formatMinutes2(minutes) {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  return `${minutes} minutes`;
}

// src/availability/public/getAvailabilityState.ts
function getPublicAvailabilityState(params) {
  const {
    date,
    timezone,
    timeMinutes,
    publicHours,
    membershipHours,
    scheduleExceptions,
    primeTimeConfig
  } = params;
  const publicSchedule = getDayScheduleForDateTz(
    date,
    timezone,
    publicHours,
    scheduleExceptions
  );
  const membershipSchedule = membershipHours ? getDayScheduleForDateTz(date, timezone, membershipHours, scheduleExceptions) : [];
  const primeTimeSchedule = primeTimeConfig ? getDayScheduleForDateTz(date, timezone, primeTimeConfig.hours, scheduleExceptions) : [];
  const isInPublic = isTimeInSegments(timeMinutes, publicSchedule);
  const isInMembership = isTimeInSegments(timeMinutes, membershipSchedule);
  const isInPrimeTime = isPrimeTime(timeMinutes, primeTimeSchedule);
  if (!isInPublic && !isInMembership) {
    return "CLOSED";
  }
  if (isInPrimeTime) {
    return "PRIME_TIME";
  }
  if (isInMembership && !isInPublic) {
    return "MEMBERSHIP";
  }
  return "PUBLIC";
}
function isTimeInSegments(timeMinutes, segments) {
  return segments.some(
    (segment) => isTimeInSegment(timeMinutes, segment)
  );
}
function getStateDescription(state) {
  const descriptions = {
    CLOSED: "Closed",
    MEMBERSHIP: "Members Only",
    PRIME_TIME: "Prime Time",
    PUBLIC: "Open"
  };
  return descriptions[state];
}
function canBookPublic(state) {
  return state === "PUBLIC" || state === "PRIME_TIME";
}
function canBookMember(state) {
  return state !== "CLOSED";
}
function isPremiumPricing(state) {
  return state === "PRIME_TIME";
}
function getBookingPermission(state) {
  if (state === "CLOSED") {
    return "none";
  }
  if (state === "MEMBERSHIP") {
    return "members";
  }
  return "public";
}

// src/availability/public/clubStatus.ts
function getClubClosingStatus(params) {
  const {
    currentTime,
    publicHours,
    scheduleExceptions,
    timezone,
    warningThresholdMinutes = 60
  } = params;
  const clubDate = utcToClubTime(currentTime, timezone);
  const schedule = getScheduleForDate(currentTime, publicHours, scheduleExceptions, timezone);
  if (isClosedAllDay(schedule)) {
    return {
      isClosingSoon: false,
      closingTime: null,
      minutesUntilClose: null,
      message: null
    };
  }
  const currentMinutes = clubDate.getHours() * 60 + clubDate.getMinutes();
  let closingMinutes = null;
  for (const segment of schedule) {
    if (currentMinutes >= segment.startMinutes && currentMinutes < segment.endMinutes) {
      closingMinutes = segment.endMinutes;
      break;
    }
  }
  if (closingMinutes === null) {
    return {
      isClosingSoon: false,
      closingTime: null,
      minutesUntilClose: null,
      message: null
    };
  }
  const minutesUntilClose = closingMinutes - currentMinutes;
  if (minutesUntilClose <= warningThresholdMinutes) {
    const closingHours = Math.floor(closingMinutes / 60);
    const closingMins = closingMinutes % 60;
    const closingTime = `${closingHours.toString().padStart(2, "0")}:${closingMins.toString().padStart(2, "0")}`;
    return {
      isClosingSoon: true,
      closingTime,
      minutesUntilClose,
      message: "Club is closing soon. Bookings may not be available."
    };
  }
  return {
    isClosingSoon: false,
    closingTime: null,
    minutesUntilClose,
    message: null
  };
}

// src/availability/public/timeFormatting.ts
function formatTimeForDisplay(time, timezone, options) {
  const fmt = options?.format || "12h";
  if (typeof time === "string") {
    const [hours, minutes] = time.split(":").map(Number);
    if (fmt === "24h") {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    const hourOfDay = hours % 24;
    const period = hourOfDay >= 12 ? "PM" : "AM";
    const displayHours = hourOfDay % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
  }
  return formatClubTime(time, timezone, fmt === "24h");
}
function formatDurationForDisplay(minutes) {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }
  const hours = minutes / 60;
  if (hours === 1) {
    return "1 hour";
  }
  if (hours % 1 === 0) {
    return `${hours} hours`;
  }
  return `${hours} hours`;
}
function getRelativeTimeMessage(fromTime, toTime, options) {
  const diffMs = toTime.getTime() - fromTime.getTime();
  const diffMinutes = Math.floor(diffMs / (1e3 * 60));
  if (diffMinutes < 0) {
    const pastMinutes = Math.abs(diffMinutes);
    if (pastMinutes < 60) {
      return `${pastMinutes} minutes ago`;
    }
    const hours2 = Math.floor(pastMinutes / 60);
    return `${hours2} ${hours2 === 1 ? "hour" : "hours"} ago`;
  }
  if (diffMinutes < 60) {
    const unit2 = options?.shortForm ? "min" : "minutes";
    return `in ${diffMinutes} ${unit2}`;
  }
  const hours = Math.floor(diffMinutes / 60);
  const unit = options?.shortForm ? "hr" : hours === 1 ? "hour" : "hours";
  return `in ${hours} ${unit}`;
}

// src/availability/public/bayStatus.ts
function getBayOccupancyStatus(params) {
  const { currentTime, bookings, timezone } = params;
  const currentBooking = bookings.find(
    (booking) => booking.startTime <= currentTime && booking.endTime > currentTime
  );
  if (!currentBooking) {
    return {
      isOccupied: false,
      occupiedUntil: null,
      nextAvailableTime: null,
      currentBooking: null
    };
  }
  const occupiedUntil = formatTimeForDisplay(asUtcDate(currentBooking.endTime), timezone, { format: "24h" });
  return {
    isOccupied: true,
    occupiedUntil,
    nextAvailableTime: occupiedUntil,
    currentBooking
  };
}
function getBayAvailableUntil(params) {
  const { currentTime, publicHours, scheduleExceptions, bookings, timezone } = params;
  const occupancy = getBayOccupancyStatus({ currentTime, bookings, timezone });
  if (occupancy.isOccupied) {
    return {
      isAvailable: false,
      availableUntil: null,
      reason: null,
      minutesAvailable: null
    };
  }
  const clubDate = utcToClubTime(currentTime, timezone);
  const schedule = getScheduleForDate(currentTime, publicHours, scheduleExceptions, timezone);
  if (isClosedAllDay(schedule)) {
    return {
      isAvailable: false,
      availableUntil: null,
      reason: null,
      minutesAvailable: null
    };
  }
  const nextBooking = bookings.filter((b) => b.startTime > currentTime).sort((a, b) => a.startTime.getTime() - b.startTime.getTime())[0];
  const currentMinutes = clubDate.getHours() * 60 + clubDate.getMinutes();
  let closingMinutes = null;
  for (const segment of schedule) {
    if (currentMinutes >= segment.startMinutes && currentMinutes < segment.endMinutes) {
      closingMinutes = segment.endMinutes;
      break;
    }
  }
  if (closingMinutes === null) {
    return {
      isAvailable: false,
      availableUntil: null,
      reason: null,
      minutesAvailable: null
    };
  }
  let availableUntilTime = null;
  let reason = null;
  const buildClosingTimeUtc = (minutes) => {
    const zoned = new Date(clubDate);
    zoned.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return clubTimeToUtc(asZonedDate(zoned), timezone);
  };
  if (nextBooking && closingMinutes) {
    const closingTimeUtc = buildClosingTimeUtc(closingMinutes);
    if (nextBooking.startTime < closingTimeUtc) {
      availableUntilTime = nextBooking.startTime;
      reason = "next-booking";
    } else {
      availableUntilTime = closingTimeUtc;
      reason = "club-closes";
    }
  } else if (nextBooking) {
    availableUntilTime = nextBooking.startTime;
    reason = "next-booking";
  } else if (closingMinutes) {
    availableUntilTime = buildClosingTimeUtc(closingMinutes);
    reason = "club-closes";
  }
  if (!availableUntilTime) {
    return {
      isAvailable: true,
      availableUntil: null,
      reason: null,
      minutesAvailable: null
    };
  }
  const minutesAvailable = Math.floor(
    (availableUntilTime.getTime() - currentTime.getTime()) / (1e3 * 60)
  );
  return {
    isAvailable: true,
    availableUntil: formatTimeForDisplay(asUtcDate(availableUntilTime), timezone, { format: "24h" }),
    reason,
    minutesAvailable
  };
}

// src/availability/public/bayDisplay.ts
function getBayDisplayStatus(params) {
  const {
    currentTime,
    publicHours,
    scheduleExceptions,
    bookings,
    timezone,
    closingWarningMinutes = 60
  } = params;
  const clubDate = utcToClubTime(currentTime, timezone);
  const schedule = getScheduleForDate(currentTime, publicHours, scheduleExceptions, timezone);
  if (isClosedAllDay(schedule)) {
    return {
      status: "closed",
      badge: {
        text: "Closed today",
        variant: "neutral"
      },
      subtitle: null,
      isBookable: false,
      metadata: {}
    };
  }
  const currentMinutes = clubDate.getHours() * 60 + clubDate.getMinutes();
  const firstSegment = schedule.length > 0 ? schedule[0] : null;
  if (firstSegment && currentMinutes < firstSegment.startMinutes) {
    const openingHours = Math.floor(firstSegment.startMinutes / 60);
    const openingMins = firstSegment.startMinutes % 60;
    const openingTime = `${openingHours.toString().padStart(2, "0")}:${openingMins.toString().padStart(2, "0")}`;
    return {
      status: "closed",
      badge: {
        text: `Opens at ${openingTime}`,
        variant: "neutral"
      },
      subtitle: null,
      isBookable: false,
      metadata: {
        opensAt: openingTime
      }
    };
  }
  const occupancy = getBayOccupancyStatus({ currentTime, bookings, timezone });
  if (occupancy.isOccupied) {
    return {
      status: "occupied",
      badge: {
        text: `Unavailable until ${occupancy.occupiedUntil}`,
        variant: "error",
        icon: "\u{1F534}"
      },
      subtitle: `Next tee time: ${occupancy.nextAvailableTime}`,
      isBookable: false,
      metadata: {
        occupiedUntil: occupancy.occupiedUntil || void 0
      }
    };
  }
  const clubStatus = getClubClosingStatus({
    currentTime,
    publicHours,
    scheduleExceptions,
    timezone,
    warningThresholdMinutes: closingWarningMinutes
  });
  const availability = getBayAvailableUntil({
    currentTime,
    publicHours,
    scheduleExceptions,
    bookings,
    timezone
  });
  if (clubStatus.isClosingSoon) {
    return {
      status: "closing-soon",
      badge: {
        text: `Available until ${availability.availableUntil || clubStatus.closingTime}`,
        variant: "warning",
        icon: "\u26A0\uFE0F"
      },
      subtitle: "Club closing soon",
      isBookable: true,
      metadata: {
        availableUntil: availability.availableUntil || void 0,
        closingTime: clubStatus.closingTime || void 0,
        minutesUntilClose: clubStatus.minutesUntilClose || void 0
      }
    };
  }
  return {
    status: "available",
    badge: {
      text: `Available until ${availability.availableUntil || "close"}`,
      variant: "success",
      icon: "\u2705"
    },
    subtitle: null,
    isBookable: true,
    metadata: {
      availableUntil: availability.availableUntil || void 0
    }
  };
}

export { AudienceEntrySchema, BayAccessScheduleSchema, BayAccessSegmentSchema, DAYS_OF_WEEK, DEFAULT_LESSON_DURATION_MINUTES, DateStringSchema, DayScheduleSchema, DemoBookingConfigSchema, MAX_DEMO_ADVANCE_MINUTES, MAX_LESSON_DURATION_MINUTES, PAST_START_API_REFUSAL, START_PASSED_REFUSAL, ScheduleExceptionKindSchema, ScheduleExceptionSchema, ScheduleExceptionsSchema, TimeSegmentSchema, UpdateDemoBookingSettingsBodySchema, WeeklyScheduleSchema, advanceWindowClearsNotice, canBookMember, canBookPublic, canStartAt, checkAvailability, checkSessionLength, create24_7Schedule, createDateFromMinutes, createFullDaySegment, describeScheduleException, earliestBookableStart, exceptionNoticeFor, filterAvailableSlots, findConflictingBookings, findNextAvailableSlot, firstOnGrid, formatDateKeyTz, formatDurationForDisplay, formatTime, formatTimeForDisplay, generateBookingSlots, getAvailableSegments, getBayAvailableUntil, getBayDisplayStatus, getBayOccupancyStatus, getBookingPermission, getBookingsForDate, getClubClosingStatus, getDayOfWeekTz, getDayScheduleForDateTz, getPrimeTimePercentage, getPrimeTimeScheduleForDate, getPublicAvailabilityState, getRelativeTimeMessage, getScheduleExceptionTz, getScheduleForDate, getSegmentDuration, getSegmentIntersection, getStateDescription, getTotalOpenMinutes, getTotalSegmentMinutes, getValidDurations, hasBookingConflict, hasBookingConflictForResource, hasOpenHours, hasScheduleExceptionTz, intersectDaySchedules, intersectSegments, intersectWeeklySchedules, is24_7Schedule, isBookingWithinSchedule, isClosedAllDay, isClubOpen, isFullDayClosure, isFullDaySchedule, isIntervalWithinSchedule, isIntervalWithinWindows, isOnStartGrid, isPremiumPricing, isPrimeTime, isPrimeTimeSlot, isPublicScheduleException, isRangeInSegment, isSameDayInTimezone, isSlotConflicted, isSlotInWeeklySchedule, isStartTooSoon, isStartableRow, isTimeInSegment, isValidStartMarks, isWeekdayTz, isWeekendTz, mergeDaySchedules, mergePublicAndMembershipHours, mergeSegments, mergeWeeklySchedules, minNoticeRefusal, minuteOfHour, minutesFromTime, normalizeSegments, overlapsPrimeTime, parseTimeToMinutes, plainGridRemainderAt, resolveAdvanceBookingDays, resolveOpenWindows, resolveOpenWindowsFrom, scheduleExceptionNotice, segmentsOverlap, sessionLengthMessage, slotInstant, startGridOptions, startMarksAt, startMarksCoverage, startMarksLabel, startMarksRemainder, subtractDaySchedules, timeFromMinutes, timeRangesOverlap, toClubMinutes, toPublicScheduleExceptions, validateDurationsFromConstraints };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map